import { DataTableColumnHeader, StatusChip } from '@/components/core';
import { Button } from '@/components/ui-kit/button';
import { ArrowCounterClockwise } from '@phosphor-icons/react';
import { ColumnDef } from '@tanstack/react-table';
import { ToilRequest } from '../../types/toil.types';
import { formatToilDate } from '../../utils/toil-date';

/** Data written before this categorization existed has no `ToilCategory` — treated as
 * `WeekendToil` here too, matching the legacy `ToilCalculationHelper`'s fallback for old rows. */
const categoryLabelKey = (category?: ToilRequest['ToilCategory']): string =>
  category === 'SpecialToil' ? 'SPECIAL_TOIL' : 'WEEKEND_TOIL';

interface CreateToilTableColumnsProps {
  t: (key: string) => string;
  onWithdraw: (toilRequest: ToilRequest) => void;
  /** Whether the signed-in user holds `hrm::toil::request` (`HRM_PERMISSIONS.TOIL.REQUEST`), which
   * TOIL_WITHDRAW also requires; the workflow enforces it regardless, this only offers or hides the button. */
  canWithdraw: boolean;
}

export const createToilTableColumns = ({
  t,
  onWithdraw,
  canWithdraw,
}: CreateToilTableColumnsProps): ColumnDef<ToilRequest>[] => [
  {
    id: 'ToilDate',
    meta: { filter: { type: 'date' } },
    header: ({ column }) => <DataTableColumnHeader column={column} title={t('TOIL_DATE')} />,
    accessorFn: (row) => row.ToilDate,
    cell: ({ row }) => formatToilDate(row.original),
  },
  {
    id: 'ToilCategory',
    meta: {
      filter: {
        type: 'options',
        options: [
          { value: 'WeekendToil', label: t('WEEKEND_TOIL') },
          { value: 'SpecialToil', label: t('SPECIAL_TOIL') },
        ],
      },
    },
    header: ({ column }) => <DataTableColumnHeader column={column} title={t('TOIL_CATEGORY')} />,
    accessorFn: (row) => row.ToilCategory,
    cell: ({ row }) => (
      <StatusChip tone={row.original.ToilCategory === 'SpecialToil' ? 'info' : 'muted'}>
        {t(categoryLabelKey(row.original.ToilCategory))}
      </StatusChip>
    ),
  },
  {
    id: 'ToilType',
    // Card: under the date.
    meta: {
      mobile: 'subtitle',
      filter: {
        type: 'options',
        options: [
          { value: 'FullDay', label: t('FULL_DAY') },
          { value: 'HalfDay', label: t('HALF_DAY') },
        ],
      },
    },
    header: ({ column }) => <DataTableColumnHeader column={column} title={t('TOIL_TYPE')} />,
    accessorFn: (row) => row.ToilType,
    cell: ({ row }) => (
      <span>{row.original.ToilType === 'FullDay' ? t('FULL_DAY') : t('HALF_DAY')}</span>
    ),
  },
  {
    id: 'Duration',
    header: ({ column }) => <DataTableColumnHeader column={column} title={t('DURATION')} />,
    accessorFn: (row) => row.Duration,
  },
  {
    id: 'Reason',
    // Card: in the footer, as in the old portal.
    meta: { filter: { type: 'text' }, mobile: 'footer' },
    header: ({ column }) => <DataTableColumnHeader column={column} title={t('REASON')} />,
    accessorFn: (row) => row.Reason?.trim() || '-',
  },
  {
    id: 'Status',
    meta: {
      filter: {
        type: 'options',
        options: ['Pending', 'Approved', 'Rejected'].map((status) => ({
          value: status,
          label: t(status.toUpperCase()),
        })),
      },
    },
    header: ({ column }) => <DataTableColumnHeader column={column} title={t('STATUS')} />,
    accessorFn: (row) => row.Status,
    cell: ({ row }) => (
      <StatusChip status={row.original.Status}>{t(row.original.Status.toUpperCase())}</StatusChip>
    ),
  },
  {
    // Header-less action column: only a pending request can be withdrawn.
    id: 'actions',
    header: () => null,
    enableSorting: false,
    enableHiding: false,
    cell: ({ row }) =>
      canWithdraw && row.original.Status === 'Pending' ? (
        <Button
          variant="ghost"
          size="icon"
          onClick={() => onWithdraw(row.original)}
          aria-label={t('WITHDRAW')}
          title={t('WITHDRAW')}
        >
          <ArrowCounterClockwise className="size-4" />
        </Button>
      ) : null,
  },
];
